<script setup>
import { computed, useId } from 'vue';

const props = defineProps({
  hint: {
    type: String,
    default: null
  }
});

// Unique id per instance + shared global state so only one hint panel
// is ever open at a time — opening one collapses any other.
const id = useId();
const activeHint = useState('activeHintContainer', () => null);

const isOpen = computed(() => activeHint.value === id);

function toggle() {
  activeHint.value = isOpen.value ? null : id;
}
</script>

<template>
  <div class="hint-container">
    <slot></slot>
    <button
        v-if="hint != null"
        type="button"
        class="hint-toggle ml-2"
        :class="{ 'hint-toggle--open': isOpen }"
        :aria-expanded="isOpen"
        @click="toggle"
    >
      Help
      <v-icon size="14" class="hint-toggle__chevron">mdi-chevron-down</v-icon>
    </button>
    <v-expand-transition>
      <div v-if="hint != null && isOpen" class="hint-panel">
        {{ hint }}
      </div>
    </v-expand-transition>
  </div>
</template>

<style scoped lang="scss">
.hint-toggle {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-top: 2px;
  padding: 2px 0;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  color: rgb(var(--v-theme-error));
  background: transparent;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.2s ease;

  &:hover {
    color: rgba(var(--v-theme-error), 0.7);
  }
}

.hint-toggle__chevron {
  transition: transform 0.2s ease;
}

.hint-toggle--open .hint-toggle__chevron {
  transform: rotate(180deg);
}

.hint-panel {
  margin-top: 6px;
  padding: 8px 10px;
  font-size: 13px;
  line-height: 1.4;
  color: rgb(var(--v-theme-on-surface));
  background: rgba(var(--v-theme-error), 0.05);
  border: 1px solid rgba(var(--v-theme-error), 0.25);
  border-radius: 6px;
}
</style>
